<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-4 col-sm-6">
          <h5>Students</h5>
        </div>
        <div class="col-8 col-sm-6 text-right">
          <div class="d-inline-flex flex-row">
            <div class="mr-2">
              <export-student :initialSession="selectedSession" :initialClass="selectedClass" :initialSection="selectedSection" :initialSearch="search" />
            </div>
            <div class="mr-2">
              <bulk-update-student v-if="pagepermission.insertable"/>
            </div>
            <div class="mr-2">
              <bulk-upload-student v-if="pagepermission.insertable" @created="handleCreated"/>
            </div>
            <div>
              <add-student v-if="pagepermission.insertable" @created="handleCreated"/>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row justify-content-center" v-if="pagepermission.viewable">
      <div class="col-xl-12">
        <div class="row align-items-center mb-2">
          <!-- Session Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              optionFilterProp="children"
              style="width: 100%"
              @change="handleSessionChange"
              :filterOption="false"
              :defaultValue="currentSession ? currentSession.id : ''"
            >
            <template v-for="schoolSession in schoolSessions">
              <a-select-option :value="schoolSession.id" :key="schoolSession.id">
                  {{schoolSession.name}}
                  <small class="text-primary ml-2">{{ currentSession.id === schoolSession.id ? 'Current' : '' }}</small>
              </a-select-option>
            </template>
            </a-select>
          </div>
          <!-- Class Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="classSpinning">
              <a-select
                size="large"
                v-model="selectedClass"
                placeholder="All Classes"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleClassSelect"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined">All Classes</a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                  {{schoolClass.name}}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="sectionSpinning">
              <a-select
              size="large"
                v-model="selectedSection"
                placeholder="All Sections"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleSectionChange"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="undefined">All Sections</a-select-option>
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                  {{section.name}}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Student Search -->
          <div class="col-md-6 col-12 mb-2" v-if="!selectedRowKeys.length">
            <div class="text-right">
              <a-input-search
                placeholder="Search Students.."
                size="large"
                v-model="search"
                @search="searchStudents"
                @change="searchStudents"
              />
            </div>
          </div>
          <div class="col-md-6 col-12 mb-2 text-md-right" v-else-if="pagepermission.deletable" >
            <a href="javascript:;" class="btn btn btn-danger btn-with-addon"
              @click="handleDeleteSelected"
            >
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-trash" />
              </span>
              Delete Selected
            </a>
          </div>
          <div class="col-md-1 col-12 mb-2 ml-auto">
            <a-tooltip title="Additional Columns">
            <a-button
            size="large"
            shape="circle"
            icon="setting"
            @click="showColumnSettings = true"
          >
          </a-button>
          </a-tooltip>
        </div>
        <!-- Add drawer for column selection -->
        <a-drawer
        title="Select Columns"
        placement="right"
        :visible="showColumnSettings"
        @close="showColumnSettings = false"
        width="300"
        >
        <div class="p-3">
          <a-checkbox-group v-model="selectedExtraColumns" style="width: 100%">
            <a-checkbox
              v-for="option in includeFields"
              :key="option.key"
              :value="option.key"
              style="display: block; margin: 10px 0"
            >
            {{ option.title }}
            </a-checkbox>
          </a-checkbox-group>
        </div>
        </a-drawer>
      </div>
        <div class="card">
          <div class="card-body">
            <students-table :rolesetting="pagepermission" :data="data" :loading="loading" :session="selectedSession" :extraColumns="selectedExtraColumns"
            :includeFields="includeFields"  @updateTable="handleUpdateTable" @selectChange="handleTableSelect"/>
          </div>
        </div>
        <div class="text-right">
          <a-pagination
              showSizeChanger
              @showSizeChange="onShowSizeChange"
              :total="meta.total"
              :pageSize.sync="pageSize"
              v-model="current"
              @change="onChange"
            >
          </a-pagination>
        </div>
      </div>
    </div>
    <div class="row justify-content-center" v-else>
      <template>
        <div>
          <a-spin :spinning="permissionCheckSpinning">
            <p class="error" v-if="!permissionCheckSpinning">
              You don't have permission to use this feature.
            </p>
          </a-spin>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import StudentsTable from '@/components/student/Table/index'
import AddStudent from '@/components/student/Forms/Add/index'
import BulkUploadStudent from '@/components/student/Forms/Bulk/index'
import BulkUpdateStudent from '@/components/student/Forms/Bulk/bulkUpdate'
import ExportStudent from '@/components/student/Forms/Export/index'
import { includeFields } from '@/services/globaldata'

import { debounce, find } from 'lodash'
// const pagename = 'student'
export default {
  components: {
    StudentsTable,
    AddStudent,
    BulkUploadStudent,
    BulkUpdateStudent,
    ExportStudent,
  },
  beforeMount: function () {
    this.permissionCheckSpinning = true
    let rc = this.$roleutility.checkpermission('student', 22)
    rc.then(data => {
      console.log('RoleCheck!Data processed. ', data)
      this.pagepermission = data.pagePermission
      this.permissionCheckSpinning = false
    })
  },
  mounted: function() {
    this.$http.get('/api/session')
      .then(({ data }) => {
        this.schoolSessions = data.data
        this.selectedSession = find(data.data, ['is_current', 1]).id
        this.getClasses()
        this.getStudents()
      })
      .catch(error => {
        console.log(error)
        this.$neoNotify.error(error)
      })
  },
  created() {
    this.searchStudents = debounce(this.searchStudents, 500)
  },
  computed: {
    currentSession: function() {
      const session = find(this.schoolSessions, ['is_current', 1])
      return session
    },
  },
  watch: {
    currentSession: function (val) {
      this.selectedSession = val.id
    },
  },
  data: function() {
    return {
      loading: true,
      visible: false,
      data: [],
      meta: {},
      schoolClasses: [],
      sections: [],
      schoolSessions: [],
      showColumnSettings: false,
      selectedExtraColumns: [],
      includeFields,
      current: 1,
      pageSize: 10,
      selectedClass: undefined,
      selectedSection: undefined,
      selectedSession: undefined,
      sectionSpinning: false,
      classSpinning: false,
      search: null,
      selectedRowKeys: [],
      pagepermission: {
        insertable: false,
        editable: false,
        viewable: false,
        deletable: false,
      },
      sorterField: null,
      sorterOrder: null,
      filters: [],
      permissionCheckSpinning: true,
    }
  },
  methods: {
    handleTableSelect: function(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },

    getStudents: function(isSearch) {
      this.selectedRowKeys = []
      this.loading = true

      let page = this.current
      if (isSearch) {
        page = 1
        this.current = 1
      }

      this.$http.get('/api/student', {
        params: {
          limit: this.pageSize,
          page: page,
          search: this.search,
          class_id: this.selectedClass,
          section_id: this.selectedSection,
          session_id: this.selectedSession,
          sorter_field: this.sorterField,
          sorter_order: this.sorterOrder,
          ...this.filters,
        },
      }).then(({ data }) => {
        this.loading = false
        this.data = data.data
        this.meta = data.meta
      })
    },
    searchStudents: function(e) {
      this.search = e.target.value
      this.getStudents(true)
    },
    handleCreated: function() {
      this.getStudents()
    },
    handleUpdateTable: function(args) {
      console.log('args', args)
      if (args !== undefined && args !== null && args.length > 0) {
        this.sorterField = args[0]
        this.sorterOrder = args[1]
        this.filters = args[2]
      }
      this.getStudents()
    },
    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
      this.getStudents()
    },
    onChange(current) {
      this.current = current
      this.getStudents()
    },
    getClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', {
        params: {
          session_id: this.selectedSession,
        },
      })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch(error => {
          console.log(error)
        })
    },
    handleClassSelect(value) {
      this.selectedSection = undefined
      if (value) {
        this.sectionSpinning = true
        this.getClassSections(value)
      }
      this.getStudents()
    },
    getClassSections(value) {
      this.$http.get(`/api/section/class/${value}`, {
        params: {
          session_id: this.selectedSession,
        },
      })
        .then(({ data }) => {
          this.sections = data.data
          this.sectionSpinning = false
        })
        .catch(error => {
          console.log(error)
        })
    },
    handleSectionChange (value) {
      if (value) {
        this.selectedSection = value
        this.getStudents()
      } else {
        this.selectedSection = undefined
        this.getStudents()
      }
    },
    handleSessionChange (value) {
      if (value) {
        this.selectedSession = value
      } else {
        this.selectedSession = this.currentSession
      }
      this.selectedSection = undefined
      this.selectedClass = undefined

      this.getClasses()
      this.getStudents()
    },
    handleDeleteSelected() {
      this.$confirm({
        title: `Are you sure?`,
        content: `Clicking Yes will delete the selected students?`,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          this.$nprogress.start()
          this.$http
            .delete('/api/students', {
              params: {
                student_ids: this.selectedRowKeys,
              },
            })
            .then(() => {
              this.handleUpdateTable()
              this.selectedRowKeys = []
              this.$nprogress.done()
              this.$notification['success']({
                message: 'Deleted Successfully!',
                description: 'The selected students were deleted successfully',
              })
            })
            .catch(error => {
              this.$nprogress.done()
              this.$neoNotify.error(error)
            })
        },
        onCancel: () => {
          console.log('Cancel')
        },
      })
    },
  },
}
</script>
<style>
.error { padding: 25px 5px; }
</style>
